import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, Globe, Mail, ChevronLeft, Target, Activity, Cpu, Layers, BookOpen, AlertCircle, CheckCircle, Network, Radio, ShieldCheck } from 'lucide-react';
import { useBrand } from '@/hooks/useBrand';
import { Breadcrumb } from '@/components/ui/Breadcrumb';

export function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const brand = useBrand();

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Privacy Policy' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 selection:bg-primary-500/30 overflow-x-hidden">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      {/* Background Ambience */}
      <div className="fixed inset-0 -z-10 pointer-events-none">
        <div className="absolute top-0 right-0 w-[1000px] h-[1000px] bg-primary-600/5 blur-[150px] rounded-full translate-x-1/2 -translate-y-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-emerald-600/5 blur-[120px] rounded-full -translate-x-1/2 translate-y-1/2 opacity-30" />
        <div className="absolute inset-0 bg-[url('/carbon-fibre.png')] opacity-20" />
      </div>

      <main id="main-content" role="main" className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 py-12">
        <div className="flex items-center justify-between mb-12">
          <Breadcrumb items={breadcrumbItems} onBack={() => navigate(-1)} />
          <button
            onClick={() => navigate(-1)}
            className="p-3 bg-white/5 border border-white/5 rounded-2xl hover:bg-white/10 transition-all"
          >
            <ChevronLeft className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Page Header */}
        <header className="mb-16 border-b border-gray-200 pb-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-10">
            <div>
              <div className="flex items-center gap-6 mb-4">
                <div className="w-16 h-16 bg-primary-600 rounded-xl flex items-center justify-center shadow-sm">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                  Privacy Policy
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary-600" />
                  Last Updated: December 15, 2024
                </span>
                <div className="w-px h-4 bg-gray-300" />
                <span className="flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-primary-600" />
                  GDPR Compliant
                </span>
              </div>
            </div>
            <div className="px-6 py-4 bg-white border border-gray-200 rounded-xl">
              <span className="text-xs font-medium text-gray-500 block mb-1">Version</span>
              <span className="text-lg font-bold text-emerald-600">1.0</span>
            </div>
          </div>
        </header>

        {/* Content Volumes */}
        <div className="grid gap-16">
          {/* Our Commitment */}
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-primary-600" />
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Target className="w-6 h-6 text-primary-600" />
                Our Commitment to Privacy
              </h2>
            </div>
            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
              <p className="text-lg text-gray-700 leading-relaxed">
                "At {brand.name}, we are committed to protecting your privacy and ensuring the security of your personal information.
                This Privacy Policy explains how we collect, use, and protect your information on the {brand.name} platform.
                We operate in full compliance with GDPR and international privacy regulations."
              </p>
            </div>
          </section>

          {/* Information We Collect */}
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Database className="w-6 h-6 text-blue-600" />
                Information We Collect
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-8 bg-white border border-gray-200 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Layers className="w-5 h-5 text-blue-600" />
                  Personal Information
                </h3>
                <ul className="space-y-3">
                  {[
                    'Identity Information (Name, Email)',
                    'Business Verification (Company Details)',
                    'Financial Information (Billing Details)',
                    'Authentication Data (ID Verification)',
                    'Communication Records (Platform Messages)'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-blue-300 transition-all">
                      <div className="w-2 h-2 bg-blue-600 rounded-full" />
                      <span className="text-sm text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-8 bg-white border border-gray-200 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Globe className="w-5 h-5 text-blue-600" />
                  Technical Information
                </h3>
                <ul className="space-y-3">
                  {[
                    'IP Address (Network Information)',
                    'Device Information (OS/Browser)',
                    'Usage Analytics (Feature Usage)',
                    'Search Queries (Search Terms)',
                    'Location Data (Optional)'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-blue-300 transition-all">
                      <div className="w-2 h-2 bg-blue-600 rounded-full" />
                      <span className="text-sm text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-emerald-600" />
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Eye className="w-6 h-6 text-emerald-600" />
                How We Use Your Information
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
              {[
                { title: 'Service Delivery', items: ['Facilitate User Connections', 'Process Payments', 'Provide Technical Support', 'Verify Credentials'], icon: Network, color: 'text-emerald-600' },
                { title: 'Platform Improvement', items: ['Analyze Usage Patterns', 'Improve Matching Algorithm', 'Develop New Features', 'Performance Testing'], icon: Cpu, color: 'text-emerald-600' },
                { title: 'Communication', items: ['System Notifications', 'Job Status Updates', 'Marketing (Opt-in)', 'Customer Support'], icon: Radio, color: 'text-emerald-600' },
                { title: 'Legal Compliance', items: ['Regulatory Compliance', 'Fraud Prevention', 'Legal Obligations', 'Rights Protection'], icon: ShieldCheck, color: 'text-emerald-600' }
              ].map((box, i) => (
                <div key={i} className="p-6 bg-white border border-gray-200 rounded-xl hover:border-emerald-300 transition-all shadow-sm">
                  <div className={`w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center mb-4 ${box.color}`}>
                    <box.icon className="w-6 h-6" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 mb-4 pb-3 border-b border-gray-100">{box.title}</h4>
                  <ul className="space-y-2">
                    {box.items.map((it, idx) => (
                      <li key={idx} className="text-xs text-gray-600 leading-relaxed flex items-start gap-2">
                        <span className="text-emerald-600 mt-0.5">•</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* Your Privacy Rights */}
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-primary-600" />
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Lock className="w-6 h-6 text-primary-600" />
                Your Privacy Rights
              </h2>
            </div>

            <div className="p-8 bg-primary-50 border border-primary-200 rounded-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { id: '01', title: 'Access', desc: 'Request a complete copy of your personal data.' },
                  { id: '02', title: 'Rectification', desc: 'Correct inaccurate or incomplete information.' },
                  { id: '03', title: 'Erasure', desc: 'Request deletion of your personal data.' },
                  { id: '04', title: 'Portability', desc: 'Export data in machine-readable format.' },
                  { id: '05', title: 'Objection', desc: 'Object to specific data processing activities.' },
                  { id: '06', title: 'Restriction', desc: 'Limit processing of your personal data.' }
                ].map((right, i) => (
                  <div key={i} className="flex gap-4 group/right">
                    <span className="text-2xl font-bold text-primary-300 group-hover/right:text-primary-600 transition-colors">{right.id}</span>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-1">{right.title}</h4>
                      <p className="text-xs text-gray-600">{right.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-emerald-600" />
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
                Data Security Measures
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { title: 'Encryption', desc: 'Industry-standard encryption for data in transit and at rest.', icon: Lock },
                { title: 'Access Control', desc: 'Strict authentication and 2FA requirements.', icon: Shield },
                { title: 'Monitoring', desc: '24/7 security monitoring and threat detection.', icon: Eye }
              ].map((prot, i) => (
                <div key={i} className="flex flex-col items-center text-center p-8 bg-white border border-gray-200 rounded-xl hover:border-emerald-300 transition-all shadow-sm">
                  <div className="p-4 bg-emerald-50 rounded-lg mb-4">
                    <prot.icon className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">{prot.title}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">{prot.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Information */}
          <section>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Mail className="w-6 h-6 text-blue-600" />
                Contact Us
              </h2>
            </div>

            <div className="p-8 bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="flex flex-col md:flex-row justify-between gap-8">
                <div className="flex-1">
                  <p className="text-sm text-gray-700 mb-6 leading-relaxed">
                    To exercise your privacy rights or request clarification, please contact our privacy team.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Email</span>
                      <span className="text-sm font-medium text-gray-900">{brand.email}</span>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Address</span>
                      <span className="text-sm font-medium text-gray-900 capitalize">{brand.config.legal.companyName}, {brand.config.legal.address.city}</span>
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg min-w-[280px] flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-sm font-bold text-gray-900">Data Protection Officer</span>
                  </div>
                  <span className="text-xs text-gray-600">dpo@{brand.config.website.replace('https://', '').replace('http://', '')}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="pt-8 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              Last updated: December 15, 2024 • Version 1.0
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
}